// slimCard — the ONE reduction of a captured card image to what may be saved (PROD-237, P17).
//
// Import-free, so scripts/check-profile-card-slim.mjs can execute it.
//
// ★★ WHERE IT CAME FROM. `slimCard` is useOnboardingStore's `_slimCard`, moved here verbatim
//    (onboarding imports it under its old name, and its six call sites are unchanged). Its
//    reason, as written at v228.11.6: a captured card carries the image as a base64 data URL
//    in `data`/`url`. That MUST NOT reach the profile PUT — two of them blow past Vercel's
//    ~4.5MB request limit, which returns 413 FUNCTION_PAYLOAD_TOO_LARGE (and the browser then
//    mislabels it as a CORS error). Keep only a small storage reference; if all we have is
//    base64 (storage upload unavailable), drop the image entirely rather than risk the 413.
//    The OCR'd fields are unaffected.
//
// ★★ THE DEFECT slimProfileSection REPAIRS. Onboarding slimmed; the Profile screens did not.
//    useProfileStore.syncSection sent `{ [section]: data }` and stored `[section]: data` as
//    they came, so EVERY capture on Profile → Insurance / Identification put the base64 card
//    into PUT /profile and into the persisted `orella-profile` entry, a successful upload
//    included (persistCardData keeps `data` on success).
export function slimCard(c) {
  if (!c) return null
  if (typeof c === 'string') return c.startsWith('data:') ? null : c
  if (typeof c !== 'object') return null
  const safeUrl = (typeof c.url === 'string' && !c.url.startsWith('data:')) ? c.url : null
  const path = c.storagePath || c.path || null
  if (!safeUrl && !path) return null
  return {
    ...(safeUrl ? { url: safeUrl } : {}),
    ...(path ? { storagePath: path } : {}),
    ...(c.name ? { name: c.name } : {}),
  }
}

// The card keys of the two profile sections orella-api shallow-merges (_shallow_merge_section:
// a key absent from the body is KEPT, an explicit null CLEARS it).
export const PROFILE_CARD_KEYS = Object.freeze({
  insurance: Object.freeze(['cardFront', 'cardBack']),
  identification: Object.freeze(['idFront', 'idBack']),
})

/**
 * slimProfileSection(section, data, previous) -> { body, local }
 *   body   what PUT /profile carries for the section;
 *   local  what the profile store keeps for it.
 * For each card key of insurance / identification:
 *   - null or undefined passes unchanged, so an explicit removal still clears the stored card;
 *   - a card slimCard can reduce is saved as that reduction, in body and local alike;
 *   - a card it cannot (the upload failed or is still running, a bare data: string) is LEFT
 *     OUT of body, so the server keeps the card it has (P17 Q5), and local keeps `previous`'s.
 *     An explicit null here would clear the stored card, which is why it is omitted instead.
 * Every other key, and every other section, passes through unchanged.
 */
export function slimProfileSection(section, data, previous) {
  const keys = PROFILE_CARD_KEYS[section]
  if (!keys || !data || typeof data !== 'object' || Array.isArray(data)) return { body: data, local: data }
  const body = { ...data }
  const local = { ...data }
  for (const key of keys) {
    if (!(key in data)) continue
    const v = data[key]
    if (v === null || v === undefined) continue
    const slim = slimCard(v)
    if (slim) { body[key] = slim; local[key] = slim; continue }
    delete body[key]
    if (previous && typeof previous === 'object' && key in previous) local[key] = previous[key]
    else delete local[key]
  }
  return { body, local }
}
